/* ==========================================================================
   DermaLabs — components.js
   Carousel, tabs, before/after slider, UGC videos + viewer, animated statistics,
   product hotspots, reviews "show more", order tracking hand-off.
   ========================================================================== */
(() => {
  const DL = window.DL;
  if (!DL) return;

  /* ---------- Carousel (scroll-snap + buttons + dots) ---------- */
  class DLCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.dl-carousel__track');
      if (!this.track) return;
      this.items = Array.from(this.track.children);
      this.dotsWrap = this.querySelector('[data-carousel-dots]');
      this.nav = document.querySelector(`[data-carousel-nav="${this.id}"]`) || this;
      this.prev = this.nav.querySelector('[data-carousel-prev]');
      this.next = this.nav.querySelector('[data-carousel-next]');
      this.prev && this.prev.addEventListener('click', () => this.step(-1));
      this.next && this.next.addEventListener('click', () => this.step(1));
      this.buildDots();
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(() => {
          this.raf = null;
          this.sync();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', DL.debounce(() => this.sync(), 150));
      this.sync();
    }
    get itemStep() {
      if (this.items.length < 2) return this.track.clientWidth;
      return this.items[1].offsetLeft - this.items[0].offsetLeft;
    }
    step(dir) {
      const perView = Math.max(1, Math.floor(this.track.clientWidth / this.itemStep));
      this.track.scrollBy({ left: dir * this.itemStep * perView, behavior: DL.reduceMotion ? 'auto' : 'smooth' });
    }
    buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.items.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `${i + 1} / ${this.items.length}`);
        b.addEventListener('click', () => this.track.scrollTo({ left: this.items[i].offsetLeft - this.items[0].offsetLeft, behavior: DL.reduceMotion ? 'auto' : 'smooth' }));
        this.dotsWrap.appendChild(b);
      });
      this.dots = Array.from(this.dotsWrap.children);
    }
    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      const scrollable = max > 2;
      this.classList.toggle('is-static', !scrollable);
      if (this.dotsWrap) this.dotsWrap.hidden = !scrollable;
      if (this.dots && this.dots.length) {
        const index = Math.round(this.track.scrollLeft / Math.max(1, this.itemStep));
        this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === Math.min(index, this.dots.length - 1))));
      }
    }
  }
  if (!customElements.get('dl-carousel')) customElements.define('dl-carousel', DLCarousel);

  /* ---------- Tabs ---------- */
  class DLTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = DL.qsa('[role="tab"]', this);
      this.panels = this.tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          }
        });
      });
    }
    select(index) {
      this.tabs.forEach((t, i) => {
        const on = i === index;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        if (this.panels[i]) this.panels[i].hidden = !on;
      });
      const section = this.closest('.dl-section');
      if (section) {
        DL.qsa('[data-ba-copy]', section).forEach((el) => (el.hidden = parseInt(el.dataset.baCopy, 10) !== index + 1));
      }
      DL.emit('tabs:change', { index, root: this });
    }
  }
  if (!customElements.get('dl-tabs')) customElements.define('dl-tabs', DLTabs);

  /* ---------- Before / after slider ---------- */
  class DLBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.set(this.range.value);
      this.range.addEventListener('input', () => this.set(this.range.value));
      this.addEventListener('pointerdown', (e) => this.onDown(e));
      this.addEventListener('pointermove', (e) => this.onMove(e));
      ['pointerup', 'pointercancel'].forEach((t) => this.addEventListener(t, () => this.onUp()));
      if (!DL.reduceMotion && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              io.disconnect();
              this.hint();
            }
          },
          { threshold: 0.6 }
        );
        io.observe(this);
      }
    }
    set(value) {
      const v = Math.min(100, Math.max(0, parseFloat(value)));
      this.style.setProperty('--pos', `${v}%`);
    }
    fromEvent(e) {
      const r = this.getBoundingClientRect();
      return ((e.clientX - r.left) / r.width) * 100;
    }
    onDown(e) {
      if (e.target === this.range) return;
      this.start = { x: e.clientX, y: e.clientY, type: e.pointerType };
      this.touched = true;
      if (e.pointerType === 'mouse') {
        this.dragging = true;
        this.update(e);
      }
    }
    onMove(e) {
      if (!this.start) return;
      if (!this.dragging && e.pointerType !== 'mouse') {
        const dx = Math.abs(e.clientX - this.start.x);
        const dy = Math.abs(e.clientY - this.start.y);
        if (dx > 6 && dx > dy) {
          this.dragging = true;
          try {
            this.setPointerCapture(e.pointerId);
          } catch (err) {}
        } else if (dy > 8) {
          this.start = null;
          return;
        }
      }
      if (this.dragging) this.update(e);
    }
    onUp() {
      this.dragging = false;
      this.start = null;
    }
    update(e) {
      const v = this.fromEvent(e);
      this.set(v);
      this.range.value = Math.round(v);
      this.classList.add('is-active');
    }
    hint() {
      if (this.touched) return;
      const base = parseFloat(this.range.value);
      const frames = [base, base - 12, base + 8, base];
      const start = performance.now();
      const dur = 1600;
      const tick = (now) => {
        if (this.touched) return;
        const t = Math.min(1, (now - start) / dur);
        const seg = Math.min(frames.length - 2, Math.floor(t * (frames.length - 1)));
        const local = t * (frames.length - 1) - seg;
        const ease = 0.5 - Math.cos(local * Math.PI) / 2;
        this.set(frames[seg] + (frames[seg + 1] - frames[seg]) * ease);
        if (t < 1) window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    }
  }
  if (!customElements.get('dl-before-after')) customElements.define('dl-before-after', DLBeforeAfter);

  /* ---------- UGC video cards ---------- */
  const ugcObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const card = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.55) card.onVisible();
              else card.onHidden();
            });
          },
          { threshold: [0, 0.55] }
        )
      : null;

  class DLUgcVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.soundBtn = this.querySelector('[data-ugc-sound]');
      const carousel = this.closest('dl-carousel');
      this.autoplay = !DL.reduceMotion && (!carousel || carousel.dataset.autoplay !== 'false');
      this.soundBtn &&
        this.soundBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleSound();
        });
      const open = this.querySelector('[data-ugc-open]');
      open &&
        open.addEventListener('click', () => {
          const section = this.closest('.dl-ugc');
          const viewer = section && section.querySelector('dl-ugc-viewer');
          if (viewer) viewer.open(parseInt(this.dataset.index, 10), open);
          else this.togglePlay();
        });
      ugcObserver && ugcObserver.observe(this);
      if (!ugcObserver) this.load();
    }
    disconnectedCallback() {
      ugcObserver && ugcObserver.unobserve(this);
    }
    load() {
      if (this.video && !this.video.src && this.dataset.src) {
        this.video.src = this.dataset.src;
        this.video.load();
      }
    }
    onVisible() {
      this.load();
      if (this.autoplay && !this.userPaused) this.play();
    }
    onHidden() {
      if (this.video && !this.video.paused) this.video.pause();
      this.classList.remove('is-playing');
    }
    play() {
      if (!this.video) return;
      const p = this.video.play();
      if (p && p.then) p.then(() => this.classList.add('is-playing')).catch(() => this.classList.remove('is-playing'));
    }
    togglePlay() {
      if (!this.video) return;
      this.load();
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
        this.classList.remove('is-playing');
      }
    }
    toggleSound() {
      if (!this.video) return;
      const unmute = this.video.muted;
      DL.qsa('dl-ugc-video').forEach((card) => card !== this && card.mute());
      this.video.muted = !unmute;
      this.classList.toggle('has-sound', unmute);
      this.soundBtn.setAttribute('aria-pressed', String(unmute));
      if (unmute) {
        this.load();
        this.play();
      }
    }
    mute() {
      if (!this.video) return;
      this.video.muted = true;
      this.classList.remove('has-sound');
      this.soundBtn && this.soundBtn.setAttribute('aria-pressed', 'false');
    }
  }
  if (!customElements.get('dl-ugc-video')) customElements.define('dl-ugc-video', DLUgcVideo);

  class DLUgcViewer extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.count = this.querySelector('[data-viewer-count]');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-viewer-close]') || e.target === this) return this.close();
        if (e.target.closest('[data-viewer-prev]')) return this.show(this.index - 1);
        if (e.target.closest('[data-viewer-next]')) return this.show(this.index + 1);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.show(this.index + 1);
        if (e.key === 'ArrowLeft') this.show(this.index - 1);
      });
      let sx = null;
      this.addEventListener('touchstart', (e) => (sx = e.touches[0].clientX), { passive: true });
      this.addEventListener(
        'touchend',
        (e) => {
          if (sx === null) return;
          const dx = e.changedTouches[0].clientX - sx;
          if (Math.abs(dx) > 50) this.show(this.index + (dx < 0 ? 1 : -1));
          sx = null;
        },
        { passive: true }
      );
    }
    get cards() {
      const section = this.closest('.dl-ugc');
      return section ? DL.qsa('dl-ugc-video', section) : [];
    }
    open(index, opener) {
      this.opener = opener;
      DL.qsa('dl-ugc-video').forEach((c) => c.video && c.video.pause());
      this.hidden = false;
      document.documentElement.classList.add('dl-lock');
      window.requestAnimationFrame(() => {
        this.setAttribute('open', '');
        this.show(index);
        const close = this.querySelector('[data-viewer-close]');
        close && close.focus({ preventScroll: true });
      });
    }
    show(i) {
      const cards = this.cards;
      if (!cards.length) return;
      this.index = ((i % cards.length) + cards.length) % cards.length;
      const card = cards[this.index];
      const poster = card.querySelector('video') && card.querySelector('video').getAttribute('poster');
      if (poster) this.video.setAttribute('poster', poster);
      this.video.src = card.dataset.src;
      this.video.muted = false;
      this.video.play().catch(() => {
        this.video.muted = true;
        this.video.play().catch(() => {});
      });
      if (this.count) this.count.textContent = `${this.index + 1} / ${cards.length}`;
    }
    close() {
      this.removeAttribute('open');
      this.video.pause();
      if (!DL.qs('dl-drawer[open]')) document.documentElement.classList.remove('dl-lock');
      window.setTimeout(() => {
        this.hidden = true;
        this.video.removeAttribute('src');
        this.video.load();
      }, DL.reduceMotion ? 0 : 400);
      this.opener && this.opener.focus({ preventScroll: true });
    }
  }
  if (!customElements.get('dl-ugc-viewer')) customElements.define('dl-ugc-viewer', DLUgcViewer);

  /* ---------- Lazy autoplay for decorative videos ---------- */
  const initAutoplayVideos = (root = document) => {
    const videos = DL.qsa('video[data-autoplay-video]:not([data-bound])', root);
    if (!videos.length) return;
    const load = (v) => {
      if (!v.src && v.dataset.src) v.src = v.dataset.src;
    };
    if (!('IntersectionObserver' in window)) {
      videos.forEach((v) => {
        v.dataset.bound = 'true';
        load(v);
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            load(v);
            if (!DL.reduceMotion) v.play().catch(() => {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.2 }
    );
    videos.forEach((v) => {
      v.dataset.bound = 'true';
      if (DL.reduceMotion) v.setAttribute('controls', '');
      io.observe(v);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAutoplayVideos());
  else initAutoplayVideos();
  document.addEventListener('shopify:section:load', (e) => initAutoplayVideos(e.target));

  /* ---------- Animated statistic (ring + count-up) ---------- */
  class DLStat extends HTMLElement {
    connectedCallback() {
      this.value = parseFloat(this.dataset.value) || 0;
      this.duration = parseInt(this.dataset.duration, 10) || 1800;
      this.number = this.querySelector('[data-stat-number]');
      this.ring = this.querySelector('[data-stat-ring]');
      this.bar = this.querySelector('[data-stat-bar]');
      if (DL.reduceMotion || !('IntersectionObserver' in window) || document.documentElement.classList.contains('shopify-design-mode')) {
        this.render(1);
        return;
      }
      this.render(0);
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            io.disconnect();
            this.animate();
          }
        },
        { threshold: 0.45 }
      );
      io.observe(this);
    }
    animate() {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / this.duration);
        const eased = 1 - Math.pow(1 - t, 3);
        this.render(eased);
        if (t < 1) window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    }
    render(progress) {
      const current = this.value * progress;
      if (this.number) this.number.textContent = Math.round(current);
      if (this.ring) {
        const c = parseFloat(this.ring.getAttribute('data-circumference'));
        this.ring.style.strokeDashoffset = String(c - (c * current) / 100);
      }
      if (this.bar) this.bar.style.transform = `scaleX(${current / 100})`;
    }
  }
  if (!customElements.get('dl-stat')) customElements.define('dl-stat', DLStat);

  /* ---------- Product hotspots ---------- */
  class DLHotspots extends HTMLElement {
    connectedCallback() {
      this.spots = DL.qsa('[data-hotspot]', this);
      this.items = DL.qsa('[data-hotspot-item]', this);
      this.spots.forEach((spot) => {
        spot.addEventListener('click', () => this.activate(spot.dataset.hotspot, true));
        spot.addEventListener('mouseenter', () => this.activate(spot.dataset.hotspot));
        spot.addEventListener('focus', () => this.activate(spot.dataset.hotspot));
      });
      this.items.forEach((item) => {
        item.addEventListener('mouseenter', () => this.activate(item.dataset.hotspotItem));
        item.addEventListener('focusin', () => this.activate(item.dataset.hotspotItem));
      });
    }
    activate(id, scroll) {
      this.spots.forEach((s) => s.setAttribute('aria-expanded', String(s.dataset.hotspot === id)));
      this.items.forEach((i) => i.classList.toggle('is-active', i.dataset.hotspotItem === id));
      if (scroll && window.matchMedia('(max-width: 989px)').matches) {
        const item = this.items.find((i) => i.dataset.hotspotItem === id);
        if (item) item.scrollIntoView({ behavior: DL.reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }
  if (!customElements.get('dl-hotspots')) customElements.define('dl-hotspots', DLHotspots);

  /* ---------- Reviews: show more ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-reviews-more]');
    if (!btn) return;
    const root = btn.closest('[data-reviews]');
    if (!root) return;
    const step = parseInt(btn.dataset.step, 10) || 4;
    const collapsed = DL.qsa('[data-review].is-collapsed', root);
    collapsed.slice(0, step).forEach((r, i) => {
      r.classList.remove('is-collapsed');
      r.style.setProperty('--i', i);
      r.classList.add('is-revealed');
    });
    if (collapsed.length <= step) btn.hidden = true;
    const shown = DL.qsa('[data-review]:not(.is-collapsed)', root).length;
    const status = root.querySelector('[data-reviews-status]');
    if (status) status.textContent = status.dataset.template.replace('[shown]', shown);
  });

  /* ---------- Order tracking → ParcelPanel hand-off ---------- */
  class DLTracking extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-tracking-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const mode = this.querySelector('[role="tab"][aria-selected="true"]');
        const byNumber = mode && mode.dataset.mode === 'number';
        const params = new URLSearchParams();
        if (byNumber) {
          const nums = this.form.querySelector('[name="nums"]').value.trim();
          if (!nums) return this.fail();
          params.set('nums', nums);
        } else {
          const order = this.form.querySelector('[name="order"]').value.trim();
          const contact = this.form.querySelector('[name="email"]').value.trim();
          if (!order || !contact) return this.fail();
          params.set('order', order);
          // ParcelPanel's own obfuscated format for the e-mail/phone value
          const token = contact.includes('@') ? contact.split('').reverse().join('').replace('@', '_-_') : contact;
          params.set('token', token);
        }
        window.location.href = `${this.dataset.endpoint}?${params.toString()}`;
      });
    }
    fail() {
      if (this.error) this.error.hidden = false;
    }
  }
  if (!customElements.get('dl-tracking')) customElements.define('dl-tracking', DLTracking);
})();
